import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Plus, 
  Users, 
  Calendar,
  BarChart3,
  Clock
} from 'lucide-react';

const QuickActions: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  type QuickAction = {
    name: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    href: string;
    shortcut?: string;
    badge?: number;
  };

  const quickActions: QuickAction[] = [
    {
      name: t('quickActions.quickBooking'),
      description: t('quickActions.quickBookingDesc'),
    icon: Plus,
    color: 'bg-primary-500 hover:bg-primary-600',
    href: '/bookings/create',
    shortcut: 'Ctrl+N',
    },
    {
      name: t('quickActions.todayBookings'),
      description: t('quickActions.todayBookingsDesc'),
    icon: Calendar,
    color: 'bg-green-500 hover:bg-green-600',
    href: '/bookings',
    },
    {
      name: t('quickActions.customerManagement'),
      description: t('quickActions.customerManagementDesc'),
    icon: Users,
    color: 'bg-blue-500 hover:bg-blue-600',
    href: '/customers',
    },
    {
      name: t('quickActions.courtStatus'),
      description: t('quickActions.courtStatusDesc'),
    icon: Clock,
    color: 'bg-purple-500 hover:bg-purple-600',
    href: '/courts',
    },
    {
      name: t('quickActions.dataReports'),
      description: t('quickActions.dataReportsDesc'),
    icon: BarChart3,
    color: 'bg-indigo-500 hover:bg-indigo-600',
    href: '/analytics',
    },
  ];

  const handleAction = (href: string) => {
    navigate(href);
  };

  return (
    <div className="card card-comfortable">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('quickActions.title')}</h3>
          <p className="text-sm text-gray-500 mt-1">{t('quickActions.subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
        {quickActions.map((action, index) => (
          <motion.button
            key={action.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            whileHover={{ 
              scale: 1.02, 
              y: -2,
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleAction(action.href)}
            className="relative group p-4 rounded-xl bg-gray-50 hover:bg-white border border-gray-200 hover:border-gray-300 hover:shadow-medium transition-all duration-200 text-left"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`
                flex items-center justify-center w-10 h-10 rounded-xl text-white transition-colors duration-200
                ${action.color}
              `}>
                <action.icon className="h-5 w-5" />
              </div>
              
              {action.badge && (
                <span className="bg-red-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                  {action.badge}
                </span>
              )}
              
              {action.shortcut && (
                <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  {action.shortcut}
                </span>
              )}
            </div>

            <h4 className="font-medium text-gray-900 mb-1 group-hover:text-primary-700 transition-colors">
              {action.name}
            </h4>
            
            <p className="text-sm text-gray-500 leading-snug">
              {action.description}
            </p>

            {/* Hover effect */}
            <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/5 to-primary-600/5"></div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default QuickActions;
